import axios from 'axios';
import {
    GET_PLAYER_PROFILE_REQUEST,
    GET_PLAYER_PROFILE_SUCCESS,
    GET_PLAYER_PROFILE_FAIL,
    GET_PLAYER_SUMMARY_REQUEST,
    GET_PLAYER_SUMMARY_SUCCESS,
    GET_PLAYER_SUMMARY_FAIL,
    GET_PLAYER_TRANSACTIONS_REQUEST,
    GET_PLAYER_TRANSACTIONS_SUCCESS,
    GET_PLAYER_TRANSACTIONS_FAIL,
    GET_PLAYER_ANALYTICS_REQUEST,
    GET_PLAYER_ANALYTICS_SUCCESS,
    GET_PLAYER_ANALYTICS_FAIL,
    GET_PLAYER_CLUBS_REQUEST,
    GET_PLAYER_CLUBS_SUCCESS,
    GET_PLAYER_CLUBS_FAIL,
    GET_PLAYER_REFERRALS_REQUEST,
    GET_PLAYER_REFERRALS_SUCCESS,
    GET_PLAYER_REFERRALS_FAIL,
    FREEZE_PLAYER_REQUEST,
    FREEZE_PLAYER_SUCCESS,
    FREEZE_PLAYER_FAIL,
    BAN_PLAYER_REQUEST,
    BAN_PLAYER_SUCCESS,
    BAN_PLAYER_FAIL,
    UNBAN_PLAYER_REQUEST,
    UNBAN_PLAYER_SUCCESS,
    UNBAN_PLAYER_FAIL,
    GET_PLAYER_PHONE_REQUEST,
    GET_PLAYER_PHONE_SUCCESS,
    GET_PLAYER_PHONE_FAIL,
    LIST_PLAYERS_REQUEST,
    LIST_PLAYERS_SUCCESS,
    LIST_PLAYERS_FAIL,
    ASSIGN_PLAYER_CLUB_REQUEST,
    ASSIGN_PLAYER_CLUB_SUCCESS,
    ASSIGN_PLAYER_CLUB_FAIL,
    REMOVE_PLAYER_CLUB_REQUEST,
    REMOVE_PLAYER_CLUB_SUCCESS,
    REMOVE_PLAYER_CLUB_FAIL,
    GET_PLAYER_REMARKS_REQUEST,
    GET_PLAYER_REMARKS_SUCCESS,
    GET_PLAYER_REMARKS_FAIL,
    UPDATE_PLAYER_REMARKS_REQUEST,
    UPDATE_PLAYER_REMARKS_SUCCESS,
    UPDATE_PLAYER_REMARKS_FAIL,
    DELETE_PLAYER_REQUEST,
    DELETE_PLAYER_SUCCESS,
    DELETE_PLAYER_FAIL,
    BULK_DELETE_PLAYERS_REQUEST,
    BULK_DELETE_PLAYERS_SUCCESS,
    BULK_DELETE_PLAYERS_FAIL,
} from '../constants/playerConstants';

const baseUrl = process.env.REACT_APP_API_URL;

// ============================================================================
// HELPER: Get auth config from state
// ============================================================================
const getAuthConfig = (getState) => {
    // Try userLogin first, then adminLogin
    const userInfo = getState().userLogin?.userInfo || getState().adminLogin?.userInfo;

    if (!userInfo?.token) {
        throw new Error('Not authorized - no token found');
    }

    return {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.token}`,
        },
    };
};

// ============================================================================
// GET PLAYER PROFILE
// ============================================================================
export const getPlayerProfile = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_PROFILE_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}`, config);

        dispatch({
            type: GET_PLAYER_PROFILE_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_PROFILE_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// GET PLAYER FINANCIAL SUMMARY
// ============================================================================
export const getPlayerFinancialSummary = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_SUMMARY_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}/summary`, config);

        dispatch({
            type: GET_PLAYER_SUMMARY_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_SUMMARY_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// GET PLAYER TRANSACTIONS (Paginated)
// ============================================================================
export const getPlayerTransactions = (
    playerId,
    page = 1,
    limit = 10,
    filters = {}
) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_TRANSACTIONS_REQUEST });

        const config = getAuthConfig(getState);

        // Build query string
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        if (filters.type) params.append('type', filters.type);
        if (filters.status) params.append('status', filters.status);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.clubId) params.append('clubId', filters.clubId);

        const { data } = await axios.get(
            `${baseUrl}/players/${playerId}/transactions?${params.toString()}`,
            config
        );

        dispatch({
            type: GET_PLAYER_TRANSACTIONS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_TRANSACTIONS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// GET PLAYER ANALYTICS
// ============================================================================
export const getPlayerAnalytics = (playerId, period = 30) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_ANALYTICS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(
            `${baseUrl}/players/${playerId}/analytics?period=${period}`,
            config
        );

        dispatch({
            type: GET_PLAYER_ANALYTICS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_ANALYTICS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// GET PLAYER CLUBS (Derived from transactions)
// ============================================================================
export const getPlayerClubs = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_CLUBS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}/clubs`, config);

        dispatch({
            type: GET_PLAYER_CLUBS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_CLUBS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// GET PLAYERS REFERRED BY THIS PLAYER
// ============================================================================
export const getPlayerReferrals = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_REFERRALS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}/referrals`, config);

        dispatch({
            type: GET_PLAYER_REFERRALS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: GET_PLAYER_REFERRALS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// FREEZE/UNFREEZE PLAYER ACCOUNT
// ============================================================================
export const freezePlayerAccount = (playerId, freeze, reason = '') => async (dispatch, getState) => {
    try {
        dispatch({ type: FREEZE_PLAYER_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.post(
            `${baseUrl}/players/${playerId}/freeze`,
            { freeze, reason },
            config
        );

        dispatch({
            type: FREEZE_PLAYER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: FREEZE_PLAYER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// GET PLAYER FULL PHONE (Sensitive - Audit Logged)
// ============================================================================
export const getPlayerFullPhone = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_PHONE_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}/phone`, config);

        dispatch({
            type: GET_PLAYER_PHONE_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_PLAYER_PHONE_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// LIST ALL PLAYERS (Paginated)
// ============================================================================
export const listPlayers = (
    page = 1,
    limit = 20,
    search = '',
    status = undefined,
    verified = undefined
) => async (dispatch, getState) => {
    try {
        dispatch({ type: LIST_PLAYERS_REQUEST });

        const config = getAuthConfig(getState);

        // Build query string
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        if (search) params.append('search', search);
        if (status !== undefined) params.append('status', status.toString());
        if (verified !== undefined) params.append('verified', verified.toString());

        const { data } = await axios.get(
            `${baseUrl}/players?${params.toString()}`,
            config
        );

        dispatch({
            type: LIST_PLAYERS_SUCCESS,
            payload: data,
        });
    } catch (error) {
        dispatch({
            type: LIST_PLAYERS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
    }
};

// ============================================================================
// ASSIGN PLAYER TO CLUB
// ============================================================================
export const assignPlayerClub = (playerId, clubId) => async (dispatch, getState) => {
    try {
        dispatch({ type: ASSIGN_PLAYER_CLUB_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.post(
            `${baseUrl}/players/${playerId}/clubs`,
            { clubIds: Array.isArray(clubId) ? clubId : [clubId] },
            config
        );

        dispatch({
            type: ASSIGN_PLAYER_CLUB_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: ASSIGN_PLAYER_CLUB_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// REMOVE PLAYER FROM CLUB
// ============================================================================
export const removePlayerClub = (playerId, clubId) => async (dispatch, getState) => {
    try {
        dispatch({ type: REMOVE_PLAYER_CLUB_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.delete(
            `${baseUrl}/players/${playerId}/clubs/${clubId}`,
            config
        );

        dispatch({
            type: REMOVE_PLAYER_CLUB_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: REMOVE_PLAYER_CLUB_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// BAN PLAYER ACCOUNT
// ============================================================================
export const banPlayerAccount = (playerId, cooldownDays, remarks) => async (dispatch, getState) => {
    try {
        dispatch({ type: BAN_PLAYER_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.post(
            `${baseUrl}/players/${playerId}/ban`,
            { cooldownDays, remarks },
            config
        );

        dispatch({
            type: BAN_PLAYER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: BAN_PLAYER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// UNBAN PLAYER ACCOUNT
// ============================================================================
export const unbanPlayerAccount = (playerId, reason = '') => async (dispatch, getState) => {
    try {
        dispatch({ type: UNBAN_PLAYER_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.post(
            `${baseUrl}/players/${playerId}/unban`,
            { reason },
            config
        );

        dispatch({
            type: UNBAN_PLAYER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: UNBAN_PLAYER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// GET PLAYER REMARKS
// ============================================================================
export const getPlayerRemarks = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: GET_PLAYER_REMARKS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.get(`${baseUrl}/players/${playerId}/remarks`, config);

        dispatch({
            type: GET_PLAYER_REMARKS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: GET_PLAYER_REMARKS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// UPDATE PLAYER REMARKS
// ============================================================================
export const updatePlayerRemarks = (playerId, text, category = 'general') => async (dispatch, getState) => {
    try {
        dispatch({ type: UPDATE_PLAYER_REMARKS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.put(
            `${baseUrl}/players/${playerId}/remarks`,
            { text, category },
            config
        );

        dispatch({
            type: UPDATE_PLAYER_REMARKS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: UPDATE_PLAYER_REMARKS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// DELETE PLAYER (Permanent - Audit Logged)
// ============================================================================
export const deletePlayer = (playerId) => async (dispatch, getState) => {
    try {
        dispatch({ type: DELETE_PLAYER_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.delete(`${baseUrl}/players/${playerId}`, config);

        dispatch({
            type: DELETE_PLAYER_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: DELETE_PLAYER_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};

// ============================================================================
// BULK DELETE PLAYERS (Permanent - Audit Logged)
// ============================================================================
export const bulkDeletePlayers = (playerIds) => async (dispatch, getState) => {
    try {
        dispatch({ type: BULK_DELETE_PLAYERS_REQUEST });

        const config = getAuthConfig(getState);
        const { data } = await axios.post(
            `${baseUrl}/players/bulk-delete`,
            { playerIds },
            config
        );

        dispatch({
            type: BULK_DELETE_PLAYERS_SUCCESS,
            payload: data,
        });

        return data;
    } catch (error) {
        dispatch({
            type: BULK_DELETE_PLAYERS_FAIL,
            payload: error.response?.data?.message || error.message,
        });
        throw error;
    }
};
